import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/layout/Layout";
import EmployeePicker from "../../components/ui/EmployeePicker";
import { evaluationApi } from "../../api/performanceApi";
import { useTemplates } from "../../api/queries/performanceQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import { currentPeriod } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import {
  PeriodFields,
  TemplateSelect,
  initialPeriod,
} from "./components/CreateEvaluationDialog";
import {
  errorList,
  periodPresets,
  periodProblems,
} from "./components/evaluationUtils";
import "./performance.css";
import usePermissions from "../../hooks/usePermissions";

const ACTIVE = { status: "ACTIVE" };
const EMPTY = [];

const RESULT_TEXT = { running: "Creating…", ok: "Created", error: "Failed" };

/**
 * Create the same evaluation for many employees. There's no bulk API, so this
 * calls POST /performance/evaluations once per row, in order, and shows each
 * result (duplicates come back as 409 rows; the rest keep going).
 */
const BulkCreateEvaluationsPage = () => {
  const user = useCurrentUser();
  // HR-level (performance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("performance");
  const qc = useQueryClient();
  const presets = useMemo(() => periodPresets(currentPeriod().year), []);
  const activeTemplates = useTemplates(ACTIVE);

  const [period, setPeriod] = useState(() => initialPeriod(presets));
  const [templateId, setTemplateId] = useState(""); // "" = auto-suggest per employee
  const [defaultEvaluator, setDefaultEvaluator] = useState(null);
  const [rows, setRows] = useState([]); // { employee, evaluator, status, message, evaluationId }
  const [changingRow, setChangingRow] = useState(null);
  const [problems, setProblems] = useState([]);
  const [running, setRunning] = useState(false);

  const evaluatorFor = (row) => row.evaluator || defaultEvaluator;
  const setRow = (employeeId, changes) =>
    setRows((list) =>
      list.map((r) =>
        r.employee.id === employeeId ? { ...r, ...changes } : r,
      ),
    );

  const addEmployee = (emp) => {
    if (emp && !rows.some((r) => r.employee.id === emp.id))
      setRows((list) => [
        ...list,
        { employee: emp, evaluator: null, status: "idle" },
      ]);
  };

  const run = async (onlyFailed) => {
    const list = [...periodProblems(period)];
    if (rows.length === 0) list.push("Add at least one employee.");
    rows.forEach((r) => {
      const ev = evaluatorFor(r);
      if (ev && ev.id === r.employee.id)
        list.push(`${employeeName(r.employee)} can't be their own evaluator.`);
    });
    setProblems(list);
    if (list.length) return;

    setRunning(true);
    const todo = rows.filter((r) =>
      onlyFailed ? r.status === "error" : r.status !== "ok",
    );
    for (const r of todo) {
      setRow(r.employee.id, { status: "running", message: "" });
      try {
        const created = await evaluationApi.create({
          employeeId: r.employee.id,
          evaluatorEmployeeId: evaluatorFor(r)?.id, // empty = the employee's level-1 approver
          templateId: templateId || undefined,
          periodLabel: period.periodLabel.trim(),
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          dueDate: period.dueDate || undefined,
        });
        setRow(r.employee.id, {
          status: "ok",
          evaluationId: created.id,
          message:
            `${created.template?.code || ""} v${created.template?.version || ""}`.trim(),
        });
      } catch (err) {
        setRow(r.employee.id, {
          status: "error",
          message: errorList(err, "Could not create.").join(" "),
        });
      }
    }
    setRunning(false);
    ["perfEvaluations", "perfEvaluationCounts"].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] }),
    );
  };

  const done = rows.filter((r) => r.status === "ok").length;
  const failed = rows.filter((r) => r.status === "error").length;
  const finished = !running && done + failed > 0;

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="builder-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can create
            evaluations.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="builder-header">
          <div>
            <Link
              to="/performance/evaluations"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Evaluations
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">
              Create evaluations for many
            </h1>
            <p className="text-muted small mb-0">
              Same period and due date for everyone; each employee gets their
              own evaluation.
            </p>
          </div>
        </div>

        {problems.length > 0 && (
          <div className="alert alert-danger fs-085" role="alert">
            <ul className="mb-0 ps-3">
              {problems.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <fieldset disabled={running} className="builder-main bulk-fieldset">
          <section className="builder-card">
            <h2 className="builder-card-title">
              <i className="bi bi-calendar-range"></i> Period & form
            </h2>
            <PeriodFields
              value={period}
              onChange={setPeriod}
              presets={presets}
              idPrefix="bulk"
            />
            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label htmlFor="bulk-template" className="profile-label">
                  Template
                </label>
                <TemplateSelect
                  id="bulk-template"
                  value={templateId}
                  onChange={setTemplateId}
                  active={activeTemplates.data || EMPTY}
                  allowAuto
                />
                <div className="text-slate-500 fs-070 mt-1">
                  {templateId
                    ? "Everyone gets this form."
                    : "The server picks the best-fitting active form for each employee."}
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="bulk-evaluator" className="profile-label">
                  Evaluator for all rows
                </label>
                <EmployeePicker
                  id="bulk-evaluator"
                  companyId={user.companyId}
                  value={defaultEvaluator}
                  onChange={setDefaultEvaluator}
                  placeholder="Search evaluator name"
                />
                <div className="text-slate-500 fs-070 mt-1">
                  Optional: empty uses each employee's level-1 approver. You can
                  change it per employee below.
                </div>
              </div>
            </div>
          </section>

          <section className="builder-card">
            <h2 className="builder-card-title">
              <i className="bi bi-people"></i> Employees ({rows.length})
            </h2>
            <EmployeePicker
              id="bulk-add"
              companyId={user.companyId}
              value={null}
              onChange={addEmployee}
              excludeId={rows.map((r) => r.employee.id)}
              placeholder="Search and add employees"
            />

            {rows.length > 0 && (
              <div className="table-responsive mt-3">
                <table className="table mb-0 perf-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Evaluator</th>
                      <th>Result</th>
                      <th aria-label="Remove"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const ev = evaluatorFor(r);
                      return (
                        <tr key={r.employee.id}>
                          <td>
                            <div className="emp-name-text">
                              {employeeName(r.employee)}
                            </div>
                            <div className="emp-sub-text">
                              {r.employee.employeeId}
                            </div>
                          </td>
                          <td className="bulk-evaluator-cell">
                            {changingRow === r.employee.id ? (
                              <EmployeePicker
                                companyId={user.companyId}
                                value={null}
                                onChange={(emp) => {
                                  setRow(r.employee.id, { evaluator: emp });
                                  setChangingRow(null);
                                }}
                                excludeId={r.employee.id}
                                placeholder="Search evaluator"
                                autoFocus
                              />
                            ) : (
                              <div className="d-flex align-items-center gap-2">
                                <span className={ev ? "" : "text-slate-400"}>
                                  {ev ? employeeName(ev) : "Level-1 approver"}
                                </span>
                                {r.evaluator && (
                                  <span className="chip chip--sm">custom</span>
                                )}
                                {r.status !== "ok" && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0"
                                    onClick={() =>
                                      setChangingRow(r.employee.id)
                                    }
                                  >
                                    Change
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="bulk-result-cell">
                            {r.status !== "idle" && (
                              <span
                                className={`bulk-result bulk-result--${r.status}`}
                              >
                                {r.status === "running" && (
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    aria-hidden="true"
                                  ></span>
                                )}
                                {r.status === "ok" && (
                                  <i className="bi bi-check-circle-fill me-1"></i>
                                )}
                                {r.status === "error" && (
                                  <i className="bi bi-x-circle-fill me-1"></i>
                                )}
                                {RESULT_TEXT[r.status]}
                              </span>
                            )}
                            {r.message && (
                              <div className="fs-075 text-slate-600 mt-1">
                                {r.message}
                              </div>
                            )}
                            {r.status === "ok" && (
                              <Link
                                to={`/performance/evaluations/${r.evaluationId}`}
                                className="fs-075"
                              >
                                Open
                              </Link>
                            )}
                          </td>
                          <td className="text-end">
                            {r.status !== "ok" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-light text-danger"
                                onClick={() =>
                                  setRows((list) =>
                                    list.filter(
                                      (x) => x.employee.id !== r.employee.id,
                                    ),
                                  )
                                }
                                aria-label={`Remove ${employeeName(r.employee)}`}
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </fieldset>

        <div className="bulk-footer">
          <div className="fs-085">
            {finished ? (
              <>
                <strong className="text-success">{done} created</strong>
                {failed > 0 && (
                  <strong className="text-danger ms-2">{failed} failed</strong>
                )}
              </>
            ) : (
              <span className="text-slate-500">
                {rows.length} employee{rows.length === 1 ? "" : "s"} selected
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            {failed > 0 && !running && (
              <button
                type="button"
                className="btn btn-outline-danger profile-btn"
                onClick={() => run(true)}
              >
                Retry failed
              </button>
            )}
            {done > 0 && !running && (
              <Link
                to="/performance/evaluations"
                className="btn profile-btn profile-btn-white"
              >
                Go to evaluations
              </Link>
            )}
            <button
              type="button"
              className="btn btn-primary profile-btn"
              onClick={() => run(false)}
              disabled={running || rows.every((r) => r.status === "ok")}
            >
              {running && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Create {rows.filter((r) => r.status !== "ok").length || ""}{" "}
              evaluation
              {rows.filter((r) => r.status !== "ok").length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BulkCreateEvaluationsPage;
